/**
 * 富文本混合格式（M4-38，用户反馈"双击已提交的文字重新编辑后，拖选一部分
 * 文字，只改这部分的字体/颜色"）——纯函数，不碰 DOM，专门维护"文字工具
 * 编辑会话里，每个字符区间用什么样式"这份状态。
 *
 * 编辑器本身是原生 `<textarea>`（`text-tool.ts` 的既有设计：IME 交给浏览器，
 * 只有富文本样式是我们自己叠加的一层）。用户在 textarea 里选中一段拖动/
 * 打字/删除，这里都是"整段文字被替换成另一段文字"的特例——不追踪光标，
 * 而是每次 `input` 事件后拿旧值/新值做一次最长公共前缀+后缀的纯字符串
 * diff（`diffEdit`），精确定出"哪一段被换成了什么"，不依赖 `selectionEnd`
 * 这类事件触发时机不总是可靠的信号。
 */
import type { TextRun, VectorFill, VectorStroke } from '../../document/types';

export interface RunStyle {
  readonly fontFamily: string;
  readonly fontSize: number;
  readonly fill: VectorFill;
  readonly stroke?: VectorStroke;
}

export interface RichRun extends RunStyle {
  /** 半开区间 [start, end)，在当前全文里的字符下标（按 `[...text]` 计数，不是 UTF-16 code unit）。 */
  readonly start: number;
  readonly end: number;
}

/** 一整段文字用同一种样式——新建文本、或者从没被局部改过样式的既有文本走这条路。 */
export function runsFromText(length: number, style: RunStyle): RichRun[] {
  return length <= 0 ? [] : [{ ...style, start: 0, end: length }];
}

function styleEquals(a: RunStyle, b: RunStyle): boolean {
  return (
    a.fontFamily === b.fontFamily &&
    a.fontSize === b.fontSize &&
    JSON.stringify(a.fill) === JSON.stringify(b.fill) &&
    JSON.stringify(a.stroke) === JSON.stringify(b.stroke)
  );
}

/** 合并样式相同的相邻 run——纯粹是整洁/效率的收尾，不合并也不影响正确性。 */
function mergeAdjacent(runs: readonly RichRun[]): RichRun[] {
  const out: RichRun[] = [];
  for (const run of runs) {
    const last = out[out.length - 1];
    if (last !== undefined && last.end === run.start && styleEquals(last, run)) {
      out[out.length - 1] = { ...last, end: run.end };
    } else {
      out.push(run);
    }
  }
  return out;
}

/**
 * 最长公共前缀/后缀 diff：`oldText.slice(start, oldEnd)` 被换成了
 * `newText.slice(start, newEnd)`，前后缀之外的部分完全没变。
 * 覆盖打字/退格/粘贴/整段替换——任何"用户编辑了 textarea"的场景，
 * 不需要知道具体是哪种编辑动作触发的。
 */
export function diffEdit(
  oldText: string,
  newText: string,
): { readonly start: number; readonly oldEnd: number; readonly newEnd: number } {
  const oldChars = [...oldText];
  const newChars = [...newText];
  const minLen = Math.min(oldChars.length, newChars.length);
  let start = 0;
  while (start < minLen && oldChars[start] === newChars[start]) start++;
  let oldEnd = oldChars.length;
  let newEnd = newChars.length;
  while (oldEnd > start && newEnd > start && oldChars[oldEnd - 1] === newChars[newEnd - 1]) {
    oldEnd--;
    newEnd--;
  }
  return { start, oldEnd, newEnd };
}

/**
 * 编辑发生后重新排布 run 区间：`[start,oldEnd)` 那段旧文字被替换成了
 * 新文字 `[start,newEnd)`（长度不必相同）。新插入的文字继承"编辑点
 * 紧邻前一个字符"所在 run 的样式（找不到就用编辑点后一个字符所在 run
 * 的样式，都找不到——比如整个文本框刚清空重打——回退到 `fallbackStyle`）；
 * 编辑范围内被吃掉的 run 直接消失，范围之后的 run 整体按长度差平移。
 */
export function reflowRunsForEdit(
  runs: readonly RichRun[],
  edit: { readonly start: number; readonly oldEnd: number; readonly newEnd: number },
  fallbackStyle: RunStyle,
): RichRun[] {
  const { start, oldEnd, newEnd } = edit;
  const delta = newEnd - start - (oldEnd - start);

  // 继承样式：编辑点前一个字符所在的 run（该 run 必然在 start 之前结束，
  // 或跨过 start）；没有（编辑发生在全文最前面）就用编辑点后一个字符的样式
  const anchor =
    runs.find((r) => r.start < start && start <= r.end) ??
    runs.find((r) => r.start >= start) ??
    fallbackStyle;

  const out: RichRun[] = [];
  for (const run of runs) {
    if (run.end <= start) {
      out.push(run); // 完全在编辑范围之前，不受影响
      continue;
    }
    if (run.start >= oldEnd) {
      out.push({ ...run, start: run.start + delta, end: run.end + delta }); // 完全在编辑范围之后，整体平移
      continue;
    }
    // 与编辑范围有交集：保留范围前的部分、丢弃范围内的部分、保留范围后的部分（平移）
    if (run.start < start) out.push({ ...run, end: start });
    if (run.end > oldEnd) out.push({ ...run, start: newEnd, end: run.end + delta });
  }
  if (newEnd > start) {
    out.push({ ...anchor, start, end: newEnd });
    out.sort((a, b) => a.start - b.start);
  }
  return mergeAdjacent(out);
}

/** 把 `[start,end)` 这段的样式换成 `patch` 指定的字段，其余字段保留原样、其余区间不受影响。 */
export function applyStyleToRange(
  runs: readonly RichRun[],
  start: number,
  end: number,
  patch: Partial<RunStyle>,
): RichRun[] {
  if (start >= end) return [...runs];
  const out: RichRun[] = [];
  for (const run of runs) {
    const overlapStart = Math.max(run.start, start);
    const overlapEnd = Math.min(run.end, end);
    if (overlapStart >= overlapEnd) {
      out.push(run); // 跟 [start,end) 没有交集
      continue;
    }
    if (run.start < overlapStart) out.push({ ...run, end: overlapStart });
    out.push({ ...run, ...patch, start: overlapStart, end: overlapEnd });
    if (run.end > overlapEnd) out.push({ ...run, start: overlapEnd });
  }
  out.sort((a, b) => a.start - b.start);
  return mergeAdjacent(out);
}

/** 从既有 `TextLayer.runs`（文档模型）重建区间列表——重新编辑既有文本时用。 */
export function richRunsFromTextRuns(runs: readonly TextRun[]): RichRun[] {
  let offset = 0;
  const out: RichRun[] = [];
  for (const run of runs) {
    const length = [...run.text].length;
    if (length > 0) {
      out.push({
        fontFamily: run.fontFamily,
        fontSize: run.fontSize,
        fill: run.fill,
        ...(run.stroke === undefined ? {} : { stroke: run.stroke }),
        start: offset,
        end: offset + length,
      });
    }
    offset += length;
  }
  return out;
}

/** 把 run 区间列表切成对应的文字子串，供 `rasterizeText`/`TextRun[]` 消费。 */
export function sliceRuns(runs: readonly RichRun[], text: string): ReadonlyArray<RichRun & { readonly text: string }> {
  const chars = [...text];
  return runs.map((run) => ({ ...run, text: chars.slice(run.start, run.end).join('') }));
}
